import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { BillingChargeSubtype } from "@redotech/redo-billing-common/unified-billing/enums";
import { BalanceTypes } from "@redotech/redo-model/balance/balances";
import { Team } from "@redotech/redo-model/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input } from "@redotech/ui/form";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

const ALLOWED_CHARGE_SUBTYPES: BillingChargeSubtype[] = [
  BillingChargeSubtype.ManualAdjustment,
  BillingChargeSubtype.ChargeLabel,
  BillingChargeSubtype.VoidLabel,
  BillingChargeSubtype.VoidUsedLabel,
  BillingChargeSubtype.Refund,
  BillingChargeSubtype.Miscellaneous,
  BillingChargeSubtype.Surcharge,
  BillingChargeSubtype.ReturnLabel,
  BillingChargeSubtype.MerchantCarrierFee,
  BillingChargeSubtype.SubscriptionFee,
  BillingChargeSubtype.RedeemPoints,
  BillingChargeSubtype.Insurance,
  BillingChargeSubtype.VoidInsurance,
  BillingChargeSubtype.TransactionFailureClawback,
  BillingChargeSubtype.WmsFee,
  BillingChargeSubtype.RedoSupply,
  BillingChargeSubtype.LandedCostDuties,
  BillingChargeSubtype.LandedCostTaxes,
  BillingChargeSubtype.LandedCostFees,
  BillingChargeSubtype.VoidLandedCostDuties,
  BillingChargeSubtype.VoidLandedCostTaxes,
  BillingChargeSubtype.VoidLandedCostFees,
  BillingChargeSubtype.FreightPurchase,
  BillingChargeSubtype.VoidFreightPurchase,
  BillingChargeSubtype.CarrierPickup,
];

export const creditForm = groupInput({
  amount: input<number>(),
  description: input<string>(),
});

export const AddBalanceCreditCard = memo(function BillingCard({
  team,
}: {
  team: Team | undefined;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [balance, setBalance] = useState<number | null>(null);
  const [chargeSubtype, setChargeSubtype] = useState<BillingChargeSubtype>(
    BillingChargeSubtype.Miscellaneous,
  );
  const input = useInput(creditForm, { amount: 0, description: "" });

  const balanceLoad = useLoad(
    async (signal) => {
      if (!team) {
        return;
      }
      const balance = await rpcClient.getBalance(
        { account: BalanceTypes.OUTBOUND_LABELS, teamId: team._id },
        { signal },
      );
      if (balance?.balance) {
        setBalance(Number(balance.balance));
      }
    },
    [team],
  );

  const [addBalanceLoad, addBalance] = useTriggerLoad(async (signal) => {
    if (!team) {
      return;
    }
    const balance = await rpcClient.addBalanceCredit(
      {
        account: BalanceTypes.OUTBOUND_LABELS,
        amount: input.inputs.amount.value,
        teamId: team._id,
        chargeSubtype,
        description: input.inputs.description.value,
      },
      { signal },
    );

    input.inputs.amount.setValue(0);
    input.inputs.description.setValue("");
    if (balance?.balance) {
      setBalance(Number(balance.balance));
    }
  });

  const chargeSubtypeOptions: RedoListItem<BillingChargeSubtype>[] = useMemo(
    () =>
      ALLOWED_CHARGE_SUBTYPES.map((subtype) => ({
        id: subtype,
        type: "text" as const,
        text: subtype,
        value: subtype,
      })),
    [],
  );

  return (
    <Card title="Add Balance Transaction">
      {balanceLoad.pending ? (
        <Text>Loading...</Text>
      ) : (
        <Flex flexDirection="column">
          <Flex pb="md">
            <Text>{`Current Balance: $${balance ?? "0.00"}`}</Text>
          </Flex>
          <Flex align="center" flexDirection="row">
            <Text>Amount</Text>
            <RedoIncrementDecrement
              placeholder="0"
              prefix="$"
              setValue={(value) => input.inputs.amount.setValue(value ?? 0)}
              size="small"
              value={input.inputs.amount.value ?? 0}
            />
          </Flex>
          <Text fontSize="sm" textColor="error">
            A negative value will deduct from the balance, a positive value will
            add to the balance.
          </Text>
          <Flex align="center" flexDirection="row">
            <Text>Transaction Type:</Text>
            <RedoSingleSelectDropdownInput<BillingChargeSubtype>
              options={chargeSubtypeOptions}
              optionSelected={({ item }) => {
                setChargeSubtype(item.value);
              }}
              selectedItem={chargeSubtypeOptions.find(
                (o) => o.value === chargeSubtype,
              )}
              size="xs"
            />
          </Flex>
          <Flex flexDirection="column">
            <Text>Description:</Text>
            <RedoTextareaInputField
              placeholder="Enter transaction description"
              setValue={(value: string) =>
                input.inputs.description.setValue(value)
              }
              value={input.inputs.description.value || ""}
            />
          </Flex>
          <Flex>
            <RedoButton
              disabled={(input.inputs.amount.value ?? 0) === 0}
              hierarchy="primary"
              onClick={() => {
                addBalance();
              }}
              pending={addBalanceLoad.pending}
              size="sm"
              text="Add funds"
              theme="normal"
            />
          </Flex>
        </Flex>
      )}
    </Card>
  );
});
